import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Robot, FacebookLogo, InstagramLogo, TiktokLogo, Image as ImageIcon,
  UploadSimple, Trash, Lightning, Sparkle, CheckCircle, Clock,
} from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";
import { CampaignSelect } from "../components/CampaignSelect";
import PublishingPauseBanner from "../components/PublishingPauseBanner";

const GOLD = "#D4A24C";
const PLATFORMS = [
  { id: "facebook", label: "Facebook", icon: FacebookLogo, color: "#1877F2" },
  { id: "instagram_feed", label: "Instagram", icon: InstagramLogo, color: "#E1306C" },
  { id: "tiktok", label: "TikTok (vidéos)", icon: TiktokLogo, color: "#ff0050" },
];

export default function MarketingAutopilotModule() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [media, setMedia] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [pending, setPending] = useState([]);
  const [diag, setDiag] = useState(null);
  const [packStats, setPackStats] = useState(null);

  const load = useCallback(() => {
    api.get("/marketing/autopilot/status").then((r) => { setStatus(r.data); setSettings(r.data.settings); }).catch(() => {});
    api.get("/marketing/autopilot/media").then((r) => setMedia(r.data.media)).catch(() => {});
    api.get("/marketing/autopilot/pending-approval").then((r) => setPending(r.data.items || [])).catch(() => {});
    api.get("/poster/pack/publish-stats").then((r) => setPackStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const approvePending = async (pid) => {
    setBusy(`approve-${pid}`);
    try { await api.post(`/marketing/autopilot/pending-approval/${pid}/approve`); toast.success("Publication corrigée et reprogrammée."); load(); }
    catch { toast.error("Erreur lors de l'approbation."); }
    finally { setBusy(""); }
  };
  const rejectPending = async (pid) => {
    setBusy(`reject-${pid}`);
    try { await api.post(`/marketing/autopilot/pending-approval/${pid}/reject`); toast.success("Publication rejetée."); load(); }
    catch { toast.error("Erreur lors du rejet."); }
    finally { setBusy(""); }
  };

  const saveSettings = async (patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    try {
      const r = await api.put("/marketing/autopilot/settings", patch);
      setSettings(r.data);
      toast.success("Réglages enregistrés.");
      api.get("/marketing/autopilot/status").then((s) => setStatus(s.data)).catch(() => {});
    } catch { toast.error("Erreur."); load(); }
  };

  const togglePlatform = (id) => {
    const cur = settings.platforms || [];
    saveSettings({ platforms: cur.includes(id) ? cur.filter((p) => p !== id) : [...cur, id] });
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const r = await api.post("/marketing/autopilot/run-now");
      toast.success(`Calendrier rempli (${r.data.created || 0} créés, ${r.data.content_generated || 0} contenus). Publiés : ${r.data.published || 0}.`);
      load();
    } catch { toast.error("Erreur lors du lancement."); }
    setBusy("");
  };
  const seedStock = async () => {
    setBusy("seed");
    try {
      const r = await api.post("/marketing/autopilot/seed-stock?count=6");
      toast.success(`${r.data.created} images de marque générées (réutilisables).`);
      load();
    } catch { toast.error("Erreur génération banque."); }
    setBusy("");
  };
  const retryFailed = async () => {
    if (!window.confirm("Réessayer les publications en échec ? Elles repartiront échelonnées (~20 min d'intervalle) pour ne pas inonder le fil.")) return;
    setBusy("retry");
    try {
      const r = await api.post("/marketing/autopilot/retry-failed?limit=100");
      toast.success(`${r.data.requeued} publication(s) en échec re-programmées. ${r.data.note || ""}`);
      load();
    } catch { toast.error("Erreur lors de la re-programmation."); }
    setBusy("");
  };
  const runDiagnostic = async () => {
    setBusy("diag");
    try {
      const r = await api.get("/marketing/autopilot/diagnostic");
      setDiag(r.data);
    } catch { toast.error("Diagnostic indisponible."); }
    setBusy("");
  };
  const sanitizeAds = async () => {
    if (!window.confirm("Nettoyer toutes les publications : retirer le mot « Karaté » et corriger les liens vers institutimpactelitemartial.com ?")) return;
    setBusy("sanitize");
    try {
      const r = await api.post("/marketing/autopilot/sanitize-existing");
      toast.success(`${r.data.publications_fixed} publication(s) corrigée(s). Site officiel : ${r.data.official_website}`);
      load();
    } catch { toast.error("Erreur lors du nettoyage."); }
    setBusy("");
  };
  const addUrl = async () => {
    if (!newUrl.startsWith("http")) { toast.error("URL invalide."); return; }
    try { await api.post("/marketing/autopilot/media", { url: newUrl, kind: "image", source: "stock" }); setNewUrl(""); toast.success("Image ajoutée."); load(); }
    catch { toast.error("Erreur."); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy("upload");
    try { await api.post("/marketing/autopilot/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Média téléversé."); load(); }
    catch { toast.error("Échec du téléversement."); }
    setBusy("");
  };
  const delMedia = async (id) => { await api.delete(`/marketing/autopilot/media/${id}`); load(); };

  if (!settings) return <div className="min-h-screen bg-zinc-950 text-zinc-400 p-8">Chargement…</div>;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="autopilot-module">
      {settings?.frozen && (
        <div className="mx-6 md:mx-8 mt-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100" data-testid="autopilot-frozen-banner">
          <b>Pilote en pause depuis le {settings.frozen_at ? new Date(settings.frozen_at).toLocaleDateString("fr-CA") : "…"} — lot 0 de la reconstruction (scénario 3).</b>
          <div className="text-amber-200/80 mt-1">Plus aucune génération ni publication automatique ne part de cet écran. Les anciennes publications sont archivées en lecture seule. Le nouveau système « Brief → Contenu » le remplacera ; la Médiathèque du dojo est déjà disponible pour tes vraies photos.</div>
        </div>
      )}
      {settings && !settings.frozen && (
        <div className="mx-6 md:mx-8 mt-6 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-100 flex flex-wrap items-center justify-between gap-3" data-testid="autopilot-freeze-cta">
          <div><b>Lot 0 non appliqué ici.</b> Le gel arrête toute génération et publication automatique et archive les publications en attente (reconstruction scénario 3 approuvée).</div>
          <button type="button" data-testid="autopilot-freeze-btn" onClick={async () => { if (!window.confirm("Geler le Pilote automatique maintenant ? Plus aucune publication automatique ne partira ; les publications en attente seront archivées.")) return; try { const r = await api.post("/marketing/autopilot/lot0-gel"); toast.success(`Pilote gelé — ${r.data.archivees} publication(s) archivée(s).`); const s = await api.get("/marketing/autopilot/settings"); setSettings(s.data); } catch (e) { toast.error(e?.response?.data?.detail || "Gel impossible"); } }} className="rounded-full bg-red-500 hover:bg-red-400 text-white font-bold px-4 py-2 text-sm">❄️ Geler le Pilote (lot 0)</button>
        </div>
      )}
      <div className="px-6 md:px-8 pt-8">
        <PageHeader
          title="Pilote Auto — Réseaux sociaux"
          icon={Robot}
          breadcrumbs={[{ label: "Marketing & contenu" }, { label: "Pilote Auto Réseaux" }]}
          subtitle="Publication automatique quotidienne · variété · FB + Instagram (+ TikTok)"
          actions={
            <label className="flex items-center gap-3 cursor-pointer" data-testid="autopilot-toggle">
              <span className="text-sm font-semibold">{settings.enabled ? "Activé" : "En pause"}</span>
              <input type="checkbox" className="sr-only peer" checked={!!settings.enabled} onChange={(e) => saveSettings({ enabled: e.target.checked })} />
              <div className="w-12 h-6 bg-zinc-700 rounded-full peer-checked:bg-green-500 relative transition-colors">
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.enabled ? "translate-x-6" : ""}`} />
              </div>
            </label>
          }
        />
      </div>

      <div className="p-6 space-y-6">
        <PublishingPauseBanner />
        {/* Status */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { l: "Programmés à venir", v: status?.scheduled_upcoming ?? 0, c: GOLD, t: "stat-upcoming" },
            { l: "Publiés (total)", v: status?.published_total ?? 0, c: "#16a34a", t: "stat-published" },
            { l: "Échecs", v: status?.failed_total ?? 0, c: "#ef4444", t: "stat-failed" },
            { l: "À valider", v: status?.pending_approval_total ?? 0, c: "#f59e0b", t: "stat-pending" },
            { l: "Médias en banque", v: status?.media_count ?? 0, c: "#fff", t: "stat-media" },
          ].map((s) => (
            <div key={s.l} data-testid={`autopilot-${s.t}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="text-3xl font-black" style={{ color: s.c }}>{s.v}</div>
              <div className="text-xs text-zinc-400 mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        {/* 📦 Packs d'affiches envoyés au Studio */}
        {packStats && (
          <div className="bg-gradient-to-r from-rose-950/40 to-zinc-900 border border-rose-800/40 rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid="pack-publish-stats">
            <div className="flex items-center gap-2 text-rose-200 font-semibold">📦 Packs d'affiches publiés</div>
            <div className="flex gap-6 ml-auto text-center">
              <div><div className="text-2xl font-black text-white" data-testid="pack-stat-month">{packStats.thisMonth}</div><div className="text-[11px] text-zinc-400">Ce mois-ci</div></div>
              <div><div className="text-2xl font-black text-green-400">{packStats.published}</div><div className="text-[11px] text-zinc-400">Publiés (FB/IG)</div></div>
              <div><div className="text-2xl font-black text-amber-400">{packStats.pendingApproval}</div><div className="text-[11px] text-zinc-400">À approuver</div></div>
              <div><div className="text-2xl font-black text-zinc-300">{packStats.total}</div><div className="text-[11px] text-zinc-400">Total envoyés</div></div>
            </div>
          </div>
        )}

        {pending.length > 0 && (
          <div className="bg-amber-950/30 border border-amber-700/50 rounded-xl p-5 space-y-4" data-testid="pending-approval-panel">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-sm uppercase tracking-wide text-amber-400">🛡️ À valider avant publication ({pending.length})</h3>
            </div>
            <p className="text-xs text-amber-200/70">Ces publications ont été retenues automatiquement (anomalie détectée). Vérifie, puis approuve (le texte sera nettoyé et republié) ou rejette.</p>
            <div className="space-y-3">
              {pending.map((p) => (
                <div key={p.id} data-testid={`pending-item-${p.id}`} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex flex-col md:flex-row gap-4">
                  {p.image_url && <img src={p.image_url} alt="" className="w-full md:w-28 h-28 object-cover rounded-lg flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(p.hold_reasons || []).map((r, i) => (
                        <span key={i} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-900/60 text-red-200 border border-red-700/50">⚠️ {r}</span>
                      ))}
                    </div>
                    <p className="text-sm text-zinc-300 whitespace-pre-wrap line-clamp-4">{p.caption}</p>
                  </div>
                  <div className="flex md:flex-col gap-2 flex-shrink-0">
                    <button data-testid={`pending-approve-${p.id}`} onClick={() => approvePending(p.id)} disabled={busy === `approve-${p.id}`}
                      className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-xs font-bold disabled:opacity-50">✓ Approuver</button>
                    <button data-testid={`pending-reject-${p.id}`} onClick={() => rejectPending(p.id)} disabled={busy === `reject-${p.id}`}
                      className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-red-900 text-zinc-300 text-xs font-bold disabled:opacity-50">✕ Rejeter</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Réglages */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
          <h3 className="font-bold text-sm uppercase tracking-wide text-zinc-400">Réglages</h3>
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Publications par jour</label>
              <input type="number" min="1" max="10" data-testid="autopilot-ppd" value={settings.posts_per_day}
                onChange={(e) => saveSettings({ posts_per_day: parseInt(e.target.value) || 8 })}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 w-24 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Images IA / semaine (coût)</label>
              <input type="number" min="0" max="20" data-testid="autopilot-ai" value={settings.ai_images_per_week}
                onChange={(e) => saveSettings({ ai_images_per_week: parseInt(e.target.value) || 0 })}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 w-24 text-sm" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-zinc-400 mb-1">Courriel du rapport quotidien</label>
              <input data-testid="autopilot-email" value={settings.report_email || ""} placeholder="(défaut : ton courriel)"
                onChange={(e) => setSettings({ ...settings, report_email: e.target.value })}
                onBlur={(e) => saveSettings({ report_email: e.target.value })}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 w-full text-sm" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <CampaignSelect
                value={settings.campaign_id}
                onChange={(v) => saveSettings({ campaign_id: v })}
                label="Campagne liée (Planificateur)"
                labelClassName="block text-xs text-zinc-400 mb-1"
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 w-full text-sm text-zinc-100"
                testid="autopilot-campaign-select"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Plateformes</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => {
                const on = (settings.platforms || []).includes(p.id);
                return (
                  <button key={p.id} data-testid={`autopilot-platform-${p.id}`} onClick={() => togglePlatform(p.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${on ? "text-white" : "bg-zinc-800 text-zinc-400"}`}
                    style={on ? { background: p.color } : {}}>
                    <p.icon size={16} weight="fill" /> {p.label}
                  </button>
                );
              })}
            </div>
            {(settings.platforms || []).includes("tiktok") && (
              <p className="text-xs text-amber-400 mt-2">⚠️ TikTok publie des vidéos : ajoute des vidéos dans la banque + active les scopes video.upload/video.publish.</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <button data-testid="autopilot-run-now" onClick={runNow} disabled={busy === "run"}
              className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-zinc-950 disabled:opacity-50" style={{ background: GOLD }}>
              <Lightning size={18} weight="fill" /> {busy === "run" ? "En cours…" : "Lancer maintenant (remplir + publier les dus)"}
            </button>
            <button data-testid="autopilot-seed" onClick={seedStock} disabled={busy === "seed"}
              className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50">
              <Sparkle size={18} /> {busy === "seed" ? "Génération…" : "Générer 6 images de marque (IA, une fois)"}
            </button>
            <button data-testid="autopilot-sanitize" onClick={sanitizeAds} disabled={busy === "sanitize"}
              className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 disabled:opacity-50">
              <Sparkle size={18} /> {busy === "sanitize" ? "Nettoyage…" : "Nettoyer les pubs (Karaté / liens)"}
            </button>
            {(status?.failed_total ?? 0) > 0 && (
              <button data-testid="autopilot-retry-failed" onClick={retryFailed} disabled={busy === "retry"}
                className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold bg-red-500/20 text-red-200 hover:bg-red-500/30 disabled:opacity-50">
                <Lightning size={18} /> {busy === "retry" ? "Re-programmation…" : `Réessayer les ${status.failed_total} échecs`}
              </button>
            )}
            <button data-testid="autopilot-diagnostic-btn" onClick={runDiagnostic} disabled={busy === "diag"}
              className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50">
              <Lightning size={18} /> {busy === "diag" ? "Analyse…" : "Pourquoi ça a échoué ? (diagnostic)"}
            </button>
          </div>
          {diag && (
            <div data-testid="autopilot-diagnostic-panel"
              className={`mt-3 rounded-lg border p-4 text-sm ${diag.facebook_ok ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"}`}>
              <div className="flex items-center gap-2 font-semibold mb-2">
                {diag.facebook_ok
                  ? <span className="text-emerald-300">✅ {diag.advice}</span>
                  : <span className="text-red-300">⚠️ {diag.advice}</span>}
              </div>
              <ul className="space-y-1 text-zinc-300">
                <li>Facebook : <span className={diag.facebook_ok ? "text-emerald-300" : "text-red-300"}>{diag.facebook_ok ? `connecté (${diag.facebook_detail})` : diag.facebook_detail}</span></li>
                <li>Instagram Business : {diag.instagram_linked ? <span className="text-emerald-300">lié</span> : <span className="text-amber-300">non lié</span>}</li>
                <li>Jeton utilisé : <span className="text-zinc-400">{diag.meta_token_source}</span></li>
                <li>Dernière raison d'échec : <span className="text-zinc-400">{diag.top_failure_reason}</span></li>
              </ul>
              {!diag.facebook_ok && (
                <a href="/app/facebook" className="inline-block mt-3 px-4 py-2 rounded-lg bg-red-500/20 text-red-200 hover:bg-red-500/30 text-sm font-medium">
                  Reconnecter Facebook / Instagram →
                </a>
              )}
            </div>
          )}
        </div>

        {/* Banque de médias */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-bold text-sm uppercase tracking-wide text-zinc-400 flex items-center gap-2"><ImageIcon size={16} /> Banque de photos/vidéos ({media.length})</h3>
            <div className="flex flex-wrap items-center gap-2">
              <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="Coller une URL d'image…" data-testid="autopilot-media-url"
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm w-56" />
              <button data-testid="autopilot-media-add" onClick={addUrl} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">Ajouter URL</button>
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm cursor-pointer" data-testid="autopilot-media-upload">
                <UploadSimple size={16} /> {busy === "upload" ? "…" : "Téléverser"}
                <input type="file" accept="image/*,video/*" className="hidden" onChange={upload} />
              </label>
            </div>
          </div>
          {media.length === 0 ? (
            <p className="text-zinc-500 text-sm">Aucun média. Téléverse tes propres photos/vidéos ou clique « Générer 6 images de marque ». Sans média, l'IA générera (selon ton quota hebdo).</p>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {media.map((m) => (
                <div key={m.id} className="relative group rounded-lg overflow-hidden border border-zinc-800 aspect-square bg-zinc-950">
                  {m.kind === "video"
                    ? <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs">🎬 vidéo</div>
                    : <img src={m.url.startsWith("http") ? m.url : m.url} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute bottom-1 left-1 text-[9px] px-1 rounded bg-black/60">{m.source}</span>
                  <button onClick={() => delMedia(m.id)} className="absolute top-1 right-1 p-1 rounded bg-black/60 opacity-0 group-hover:opacity-100"><Trash size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Prochaines publications */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h3 className="font-bold text-sm uppercase tracking-wide text-zinc-400 mb-4 flex items-center gap-2"><Clock size={16} /> Prochaines publications</h3>
          {(status?.next_posts || []).length === 0 ? (
            <p className="text-zinc-500 text-sm">Aucune publication programmée. Clique « Lancer maintenant » pour remplir le calendrier.</p>
          ) : (
            <div className="space-y-2">
              {status.next_posts.map((p, i) => (
                <div key={i} className="flex items-center gap-3 text-sm border-b border-zinc-800/60 pb-2" data-testid={`autopilot-next-${i}`}>
                  {p.caption ? <CheckCircle size={16} className="text-green-500 shrink-0" weight="fill" /> : <Clock size={16} className="text-amber-500 shrink-0" />}
                  <span className="text-zinc-400 w-32 shrink-0">{new Date(p.scheduled_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}</span>
                  <span className="font-medium shrink-0">{p.angle_label}</span>
                  <span className="text-zinc-500 text-xs truncate">{(p.caption || "contenu à générer").slice(0, 60)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
